import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { body, useApi } from '@/api/client'
import { ApiError } from '@/api/errors'
import type { components } from '@/api/schema'

type Me = components['schemas']['Me']
export type Permission = Me['permissions'][number]
export type SessionState = components['schemas']['SessionState']

/**
 * Where the session stands (MRS 6.1, 7.2):
 * `none` is signed out, `mfa-setup` is a staff user with no confirmed TOTP
 * secret, `mfa-challenge` is a user whose session has not answered the TOTP
 * challenge yet, and `ready` may use the application.
 */
export type SessionStage = 'none' | 'mfa-setup' | 'mfa-challenge' | 'ready'

interface PasswordReset {
  token: string
  email: string
  password: string
  passwordConfirmation: string
}

interface InviteAcceptance {
  token: string
  name: string
  password: string
  passwordConfirmation: string
}

interface MfaEnrolment {
  otpauthUri: string
}

function stageOf(me: Me | null): SessionStage {
  if (me === null) return 'none'
  if (me.mfa.required && !me.mfa.enrolled) return 'mfa-setup'
  if (me.mfa.enrolled && !me.mfa.session_confirmed) return 'mfa-challenge'
  return 'ready'
}

/**
 * The signed-in user and every credential call (MRS 6.1). The state lives in
 * memory only: nothing here writes to localStorage, sessionStorage or a
 * cookie, and no password, code or recovery code is kept after its call
 * returns. The session itself is the HTTP-only Sanctum cookie.
 */
export const useAuthStore = defineStore('auth', () => {
  const me = ref<Me | null>(null)
  const loaded = ref(false)

  const stage = computed<SessionStage>(() => stageOf(me.value))
  const isStaff = computed(() => me.value?.is_staff === true)
  const isCustomer = computed(() => me.value?.is_staff === false)
  const mfaConfirmed = computed(() => stage.value === 'ready')
  const permissions = computed<readonly Permission[]>(() => me.value?.permissions ?? [])

  function can(permission: Permission): boolean {
    return permissions.value.includes(permission)
  }

  /**
   * Sanctum needs the XSRF-TOKEN cookie on every state-changing call, and a
   * visitor who has not signed in has none. Every credential post goes
   * through here, so the cookie is always fresh.
   */
  async function withCsrf<T>(post: () => Promise<T>): Promise<T> {
    await useApi().GET('/sanctum/csrf-cookie')
    return post()
  }

  function adopt(session: SessionState): SessionState {
    me.value = session.user
    loaded.value = true
    return session
  }

  /** Reads the session once. A 401 is the normal answer for a visitor. */
  async function fetchMe(): Promise<void> {
    try {
      me.value = body(await useApi().GET('/api/v1/me')).data
    } catch (error) {
      if (!(error instanceof ApiError) || error.status !== 401) throw error
      me.value = null
    }
    loaded.value = true
  }

  async function ensureLoaded(): Promise<void> {
    if (!loaded.value) await fetchMe()
  }

  async function signIn(email: string, password: string, remember: boolean): Promise<SessionState> {
    return withCsrf(async () =>
      adopt(
        body(await useApi().POST('/api/v1/auth/sign-in', { body: { email, password, remember } }))
          .data,
      ),
    )
  }

  async function signOut(): Promise<void> {
    try {
      await withCsrf(() => useApi().POST('/api/v1/auth/sign-out'))
    } finally {
      me.value = null
    }
  }

  async function requestMagicLink(email: string): Promise<void> {
    await withCsrf(() => useApi().POST('/api/v1/auth/magic-link', { body: { email } }))
  }

  async function consumeMagicLink(token: string): Promise<SessionState> {
    return withCsrf(async () =>
      adopt(body(await useApi().POST('/api/v1/auth/magic-link/consume', { body: { token } })).data),
    )
  }

  async function acceptInvite(invite: InviteAcceptance): Promise<SessionState> {
    return withCsrf(async () =>
      adopt(
        body(
          await useApi().POST('/api/v1/auth/invites/accept', {
            body: {
              token: invite.token,
              name: invite.name,
              password: invite.password,
              password_confirmation: invite.passwordConfirmation,
            },
          }),
        ).data,
      ),
    )
  }

  async function forgotPassword(email: string): Promise<void> {
    await withCsrf(() => useApi().POST('/api/v1/auth/password/forgot', { body: { email } }))
  }

  async function resetPassword(reset: PasswordReset): Promise<void> {
    await withCsrf(() =>
      useApi().POST('/api/v1/auth/password/reset', {
        body: {
          token: reset.token,
          email: reset.email,
          password: reset.password,
          password_confirmation: reset.passwordConfirmation,
        },
      }),
    )
  }

  /** Starts TOTP enrolment. Each call replaces the secret that is not confirmed yet. */
  async function beginMfaSetup(): Promise<MfaEnrolment> {
    return withCsrf(async () => ({
      otpauthUri: body(await useApi().POST('/api/v1/auth/mfa/setup')).data.otpauth_uri,
    }))
  }

  /**
   * Confirms the enrolment. The recovery codes go to the caller and are not
   * kept here: the API shows them once and so does the screen.
   */
  async function confirmMfaSetup(code: string): Promise<string[]> {
    const codes = await withCsrf(
      async () =>
        body(await useApi().POST('/api/v1/auth/mfa/confirm', { body: { code } })).data
          .recovery_codes,
    )
    await fetchMe()
    return codes
  }

  async function answerMfaChallenge(code: string): Promise<SessionState> {
    return withCsrf(async () =>
      adopt(body(await useApi().POST('/api/v1/auth/mfa/challenge', { body: { code } })).data),
    )
  }

  async function useRecoveryCode(recoveryCode: string): Promise<SessionState> {
    return withCsrf(async () =>
      adopt(
        body(
          await useApi().POST('/api/v1/auth/mfa/recovery', {
            body: { recovery_code: recoveryCode },
          }),
        ).data,
      ),
    )
  }

  /** Changes the name or the time zone of the signed-in user. A null zone is the zone of the helpdesk. */
  async function updateProfile(profile: { name: string; timezone: string | null }): Promise<void> {
    me.value = body(await useApi().PATCH('/api/v1/me/profile', { body: profile })).data
  }

  /**
   * Ends TOTP for a customer user (MRS 6.1: optional for customers). The API
   * asks for a code of the authenticator, so only its owner can end it.
   */
  async function disableMfa(code: string): Promise<void> {
    await useApi().POST('/api/v1/auth/mfa/disable', { body: { code } })
    await fetchMe()
  }

  /** The address of the Google consent screen (MRS 6.1: existing accounts only). */
  async function googleSignInUrl(): Promise<string> {
    const url = body(await useApi().GET('/api/v1/auth/google/redirect')).data.url
    // The browser is sent to this address, so it must be a web address and nothing else.
    if (new URL(url).protocol !== 'https:') {
      throw new Error('The API sent a Google sign-in address that is not HTTPS.')
    }
    return url
  }

  return {
    me,
    loaded,
    stage,
    isStaff,
    isCustomer,
    mfaConfirmed,
    permissions,
    can,
    fetchMe,
    ensureLoaded,
    signIn,
    signOut,
    requestMagicLink,
    consumeMagicLink,
    acceptInvite,
    forgotPassword,
    resetPassword,
    beginMfaSetup,
    confirmMfaSetup,
    answerMfaChallenge,
    useRecoveryCode,
    updateProfile,
    disableMfa,
    googleSignInUrl,
  }
})
